import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { MinusCircleIcon } from 'tdesign-icons-react';
import {
  Dialog,
  Form,
  Input,
  InputNumber,
  Link,
  MessagePlugin,
  Select,
  Textarea,
} from 'tdesign-react';
import formRulePattern from '@/utils/formRulePattern';
import { createLib, updateLib, validateProductLib } from '@/services/resourceService';
import Tip from '@/components/Tip';
import { isVoid, parseUrl, toTsOptions } from '@/utils/common';
import ValidateWrapper from '@/components/Form/ValidateWrapper';

const { FormItem, FormList } = Form;

const options = [
  {
    label: 'http',
    value: 'http',
  },
  {
    label: 'ftp（开发中）',
    value: 'ftp',
    disabled: true,
  },
  {
    label: 'jfrog（开发中）',
    value: 'jfrog',
    disabled: true,
  },
];

export default function FormModal({ visible, handleClose, detail }) {
  const isEdit = !isEmpty(detail);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const type = Form.useWatch('type', form);
  const host = Form.useWatch('host', form);
  const port = Form.useWatch('port', form);
  const path = Form.useWatch('path', form);

  const origin = useMemo(() => {
    return `${type}://${host}:${port}`;
  }, [host, port, type]);

  // 完整路径
  const url = useMemo(() => {
    return `${origin}${path || ''}`;
  }, [origin, path]);

  // 规避setFieldsValue bug
  const initialData = useMemo(() => {
    if (isEmpty(detail)) {
      return {
        type: 'http',
      };
    } else {
      const authParams = JSON.parse(detail.authParams || '{}');
      const { host, port, path } = parseUrl(detail.productLibUrl);
      return {
        ...detail,
        name: detail.productLibName,
        note: detail.productLibNote,
        type: detail.productLibType,
        host,
        port,
        path,
        key: detail.productLibKey,
        params: Object.entries(authParams).map(([key, value]) => ({ key, value })),
      };
    }
  }, [detail]);

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      const params = {
        ...fields,
        url,
        params: JSON.stringify(
          Object.fromEntries((fields.params || []).map((item) => [item.key, item.value])),
        ),
      };
      delete params.host;
      delete params.port;
      delete params.path;
      try {
        setSubmitting(true);

        if (isEdit) {
          await updateLib({
            ...params,
            id: detail.productLibId,
          });
        } else {
          await createLib(params);
        }
        MessagePlugin.success('操作成功');
        handleClose(true);
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };
  return (
    <Dialog
      visible={visible}
      onClose={() => handleClose()}
      header={isEdit ? '修改制品库' : '新建制品库'}
      destroyOnClose
      width={800}
      placement='center'
      confirmBtn={{
        loading: submitting,
        content: '确认',
      }}
      onConfirm={() => form.submit()}
    >
      <Form
        form={form}
        initialData={initialData}
        resetType='initial'
        onSubmit={handleSubmit}
        labelWidth={140}
        style={{ width: '86%' }}
      >
        <FormItem
          name='name'
          label={
            <Tip align='right' content='用于页面显示的制品库名称'>
              制品库名称
            </Tip>
          }
          rules={[{ required: true, message: '制品库名称必填' }]}
        >
          <Input />
        </FormItem>
        <FormItem
          name='key'
          label={
            <Tip align='right' content='用于系统使用标识名称，英文字母+下划线组成'>
              唯一标识符
            </Tip>
          }
          rules={[{ required: true, message: '唯一标识符必填' }, formRulePattern('name')]}
        >
          <Input />
        </FormItem>
        <FormItem label='制品库地址' requiredMark>
          <ListItem style={{ marginBottom: 0 }}>
            <FormItem style={{ flex: 6, width: 0 }} name='type' rules={[{ required: true }]}>
              <Select options={toTsOptions(options)} />
            </FormItem>
            <span style={{ lineHeight: '32px' }}>&nbsp;-&nbsp;</span>
            <FormItem style={{ flex: 10, width: 0 }} name='host' rules={[{ required: true }]}>
              <Input placeholder='IP/域名' />
            </FormItem>
            <span style={{ lineHeight: '32px' }}>&nbsp;-&nbsp;</span>
            <FormItem style={{ flex: 6, width: 0 }} name='port' rules={[{ required: true }]}>
              <ValidateWrapper
                allowEmpty
                onValidate={async () => {
                  if (isVoid(host) || isVoid(port)) {
                    MessagePlugin.warning('请先输入');
                    return;
                  }
                  const { data } = await validateProductLib(origin);
                  return data;
                }}
              >
                <InputNumber theme='normal' decimalPlaces={0} placeholder='端口号' />
              </ValidateWrapper>
            </FormItem>
            <FormItem style={{ width: '100%', marginTop: 12 }} name={'path'}>
              <Input placeholder='路径' />
            </FormItem>
          </ListItem>
        </FormItem>

        <FormItem label='制品库鉴权信息'>
          <FormList name='params'>
            {(fields, { add, remove }) => (
              <ListContainer>
                {fields.map(({ key, name, ...restField }) => (
                  <ListItem key={key}>
                    <FormItem
                      style={{ flex: 5, width: 0 }}
                      {...restField}
                      name={[name, 'key']}
                      rules={[{ required: true }]}
                    >
                      <Input placeholder='变量名' />
                    </FormItem>
                    <FormItem
                      style={{ flex: 3, width: 0, margin: '0 12px' }}
                      {...restField}
                      name={[name, 'value']}
                      rules={[{ required: true }]}
                    >
                      <Input placeholder='变量值' />
                    </FormItem>

                    <FormItem>
                      <MinusCircleIcon
                        size='20px'
                        style={{ cursor: 'pointer' }}
                        onClick={() => remove(name)}
                      />
                    </FormItem>
                  </ListItem>
                ))}

                <Link theme='primary' hover='color' onClick={() => add({ key: null, value: null })}>
                  添加
                </Link>
              </ListContainer>
            )}
          </FormList>
        </FormItem>

        <FormItem name='note' label='制品备注信息' rules={[{ required: true }]}>
          <Textarea placeholder='填写备注信息' />
        </FormItem>
      </Form>
    </Dialog>
  );
}

const ListContainer = styled.div`
  width: 100%;
`;

const ListItem = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  .t-input-number {
    width: 100%;
  }
  .t-form__item {
    margin-right: 0;
  }
`;
